import { useState } from 'react';
import type { ApiClient } from '@sentinel/api-client';
import { useImpersonator, useSession, useUser } from '../session/hooks';

export interface ImpersonationBannerMessages {
  /** "Impersonating {{target}} as {{actor}}." */
  impersonating: string;
  /** "Stop impersonating" */
  stop: string;
  /** "Stopping…" while the request is in flight. */
  stopping: string;
  /** Surfaced when the stop request fails for any non-403 reason. */
  failure: string;
}

export interface ImpersonationBannerProps {
  apiClient: ApiClient;
  messages: ImpersonationBannerMessages;
  /**
   * Optional override for the stop endpoint. Defaults to `/org/impersonate/stop`
   * (used by Console + Admin). The portal passes `/system/impersonate/self-stop`
   * so a system break-glass session self-stops via its own namespace
   * (`stopped_by: "self"` audit). Both are authorised by the impersonation
   * token's `act` claim and take no request body.
   */
  stopPath?: '/org/impersonate/stop' | '/system/impersonate/self-stop';
}

const wrapperStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '1rem',
  padding: '0.5rem 1rem',
  // Deep danger red. White text on this background is 5.6:1 — clears WCAG 2 AA
  // (4.5:1) for the banner copy. The previous `oklch(60% 0.18 28)` rendered too
  // light (4.32:1) and failed the contrast audit.
  background: '#c62828',
  color: '#fff',
  fontSize: 14,
  position: 'sticky',
  top: 0,
  zIndex: 100,
};
const buttonStyle: React.CSSProperties = {
  // Darken (not lighten) the button against the red so white text keeps ≥4.5:1;
  // a translucent-white fill would brighten the background and fail contrast.
  background: 'rgba(0, 0, 0, 0.18)',
  color: '#fff',
  border: '1px solid rgba(255, 255, 255, 0.55)',
  padding: '0.25rem 0.75rem',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
};
const failureStyle: React.CSSProperties = {
  fontSize: 12,
  opacity: 0.85,
};

/**
 * Persistent banner shown while an impersonation session is active. Mounts
 * inside `<SessionProvider>` so Console and Admin pick it up without their
 * own wiring. The banner is a no-op when `useImpersonator()` is null, so it
 * is safe to render unconditionally.
 *
 * The "stop" action POSTs to `stopPath` (default `/org/impersonate/stop`;
 * portal passes `/system/impersonate/self-stop`). Both end the session by the
 * impersonation token's `act` claim. On success we refresh the session and the
 * banner disappears.
 */
export function ImpersonationBanner({ apiClient, messages, stopPath }: ImpersonationBannerProps) {
  const session = useSession();
  const impersonator = useImpersonator();
  const user = useUser();
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!impersonator) return null;

  async function handleStop() {
    setError(null);
    setStopping(true);
    try {
      // Branch explicitly so openapi-fetch's typed overload narrows the path
      // literal (a union-typed argument may not resolve the request init type).
      // Both endpoints take no request body and end the session by the
      // impersonation token's `act` claim.
      const { error: err } =
        stopPath === '/system/impersonate/self-stop'
          ? await apiClient.POST('/system/impersonate/self-stop', {})
          : await apiClient.POST('/org/impersonate/stop', {});
      if (err) {
        setError(messages.failure);
        return;
      }
      await session.refresh();
    } catch {
      setError(messages.failure);
    } finally {
      setStopping(false);
    }
  }

  const targetEmail = user?.email ?? user?.id ?? '';
  return (
    <div role="status" aria-live="polite" style={wrapperStyle} data-testid="impersonation-banner">
      <span>
        {messages.impersonating
          .replace('{{target}}', targetEmail)
          .replace('{{actor}}', impersonator.email || impersonator.id)}
        {error ? <span style={failureStyle}> · {error}</span> : null}
      </span>
      <button
        type="button"
        onClick={() => void handleStop()}
        disabled={stopping}
        style={buttonStyle}
        data-testid="impersonation-banner-stop"
      >
        {stopping ? messages.stopping : messages.stop}
      </button>
    </div>
  );
}
